import { formatStringList } from '../decom-policy-utils.tsx';
import { decomActions } from '../../../../../shared/decom-actions.tsx';
import { styled } from '@mui/system';
import { decomNarratives } from './decom-review-narratives.tsx';
import { DecomActionModel, DecomModel } from '../../../../../../types.ts';
import SharedDetailAccordion from '../../../../../shared/shared-detail-accordion.tsx';
import SharedReviewLayout, {
  ReviewSection,
} from '../../../../../shared/shared-review-layout.tsx';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';

interface ReviewStepProps {
  selectedPolicy: DecomModel;
  pageMode?: string;
}

export default function ReviewStepOld({
  selectedPolicy,
  pageMode,
}: ReviewStepProps) {
  const theme = useTheme();

  const { currentTier, apiUrl } = useUserSettings();
  const actionsData = selectedPolicy.actions || [];
  const grouped = Object.values(actionsData ?? {})
    .flatMap(({ actions, day }) =>
      actions.map((action) => ({
        ...action,
        day,
      }))
    )
    .reduce(
      (acc, action) => {
        const { day } = action;
        if (!acc[day]) acc[day] = [];
        acc[day].push(action);
        return acc;
      },
      {} as Record<number, DecomActionModel[]>
    );

  const sortedDays = Object.keys(grouped)
    .map(Number)
    .sort((a, b) => a - b);

  const StepIcon = styled(FiberManualRecord)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: 14,
  }));

  const StepConnector = styled('div')(({ theme }) => ({
    width: 0.5,
    backgroundColor: theme.palette.grey[400],
    flexGrow: 1,
    marginLeft: 0,
  }));

  const isFormPage =
    pageMode === 'create' || pageMode === 'clone' || pageMode === 'edit';

  const sections: ReviewSection[] = [
    {
      title: 'Basic Information',
      leftWidth: {
        columnWidth: 5,
        labelWidth: 6.5,
        valueWidth: 5.5,
      },
      rightWidth: {
        columnWidth: 6.7,
        labelWidth: 6.5,
        valueWidth: 5.5,
      },
      leftItems: [
        { label: 'Policy Name', value: selectedPolicy.name },
        {
          label: 'Trigger Type',
          value:
            selectedPolicy.triggerType === 'suspension'
              ? 'User Suspension'
              : 'Org Unit Change',
        },
        {
          label: 'Act Immediately',
          value: selectedPolicy.actImmediately,
        },
        {
          label: 'Org Units',
          value:
            selectedPolicy.org && selectedPolicy.org.length > 0
              ? selectedPolicy.org.map((target) => ({
                  email: target,
                  type: 'OrgUnit' as const,
                }))
              : [],
          variant: 'chip',
          isVisible: !!(selectedPolicy.org && selectedPolicy.org.length > 0),
        },
        {
          label: 'Decommissioning ID',
          value: selectedPolicy.id,
          isVisible: !!(
            currentTier === 'enterprise' &&
            !isFormPage &&
            selectedPolicy.id
          ),
        },
        {
          label: 'API Endpoint',
          value: `${apiUrl}/v1/policy/decommissioning`,
          isVisible: !!(
            currentTier === 'enterprise' &&
            !isFormPage &&
            apiUrl &&
            apiUrl.length > 0
          ),
        },

        {
          label: 'Status',
          value: selectedPolicy.isActive ? 'active' : 'disabled',
          isVisible: !isFormPage,
        },
      ],
      rightItems: [
        {
          label: 'Send Summary Email',
          value: selectedPolicy.sendSummary,
        },
        {
          label: 'Summary Email',
          value: selectedPolicy.notificationUser
            ? [
                {
                  email: selectedPolicy.notificationUser,
                  type: 'Unknown' as const,
                },
              ]
            : [],
          isVisible: !!selectedPolicy.notificationUser,
          variant: 'chip',
        },
        {
          label: 'Leave Unsuspended',
          value: selectedPolicy.keepUnsuspended,
        },
        {
          label: 'Pause Decom On User Org Change',
          value: selectedPolicy.validateUserIsStillInDecomOrg,
        },
        {
          label: 'Pause Decom On Unsuspension Of User',
          value: selectedPolicy.validateUserIsStillSuspended,
        },
        {
          label: 'Created By',
          value: selectedPolicy.createdBy
            ? [{ email: selectedPolicy.createdBy, type: 'User' as const }]
            : [],
          variant: 'chip',
        },
        {
          label: 'Created Date',
          value: selectedPolicy.createdTime,
          variant: 'date',
          isVisible: !isFormPage,
        },
        {
          label: 'Last Updated By',
          value: selectedPolicy.lastUpdatedBy
            ? [{ email: selectedPolicy.lastUpdatedBy, type: 'User' as const }]
            : [],
          variant: 'chip',
          isVisible: !isFormPage,
        },
        {
          label: 'Last Updated Date',
          value: selectedPolicy.lastUpdatedTime,
          variant: 'date',
          isVisible: !isFormPage,
        },
      ],
    },
    {
      title: 'Action Configuration',
      leftWidth: {
        labelWidth: 3,
        valueWidth: 9,
      },
      leftItems: [
        {
          label: 'Default Emails to Notify',
          value:
            selectedPolicy.defaultUserToNotify &&
            selectedPolicy.defaultUserToNotify.length > 0
              ? selectedPolicy.defaultUserToNotify.map((email) => ({
                  email: email,
                  type: 'Unknown' as const,
                }))
              : [],
          variant: 'chip',
          isVisible: !!(
            selectedPolicy.defaultUserToNotify &&
            selectedPolicy.defaultUserToNotify.length > 0
          ),
        },
        {
          label: 'Default Destination User',
          value:
            selectedPolicy.defaultUseManagerEmail === true
              ? [{ email: 'Manager', type: 'Unknown' as const }]
              : typeof selectedPolicy.defaultDestUser === 'undefined' ||
                selectedPolicy.defaultDestUser === null
              ? 'No Default Destination User'
              : [
                  {
                    email: selectedPolicy.defaultDestUser.userEmail,
                    type: selectedPolicy.defaultDestUser.type as
                      | 'User'
                      | 'Group'
                      | 'Domain'
                      | 'gPanel Role'
                      | 'OrgUnit'
                      | 'Shared Drive'
                      | 'RegEx'
                      | 'User Drive'
                      | 'Resource Calendar'
                      | 'Unknown',
                  },
                ],
          variant: 'chip',
        },
      ],
    },
    {
      title: 'Actions',
      leftItems: [
        {
          value: sortedDays.map((day, index) => (
            <Box key={day} display="flex" mb={4}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                mr={2}
              >
                <StepIcon />
                {index < sortedDays.length && <StepConnector />}
              </Box>
              <Box flexGrow={1} sx={{ color: theme.palette.text.secondary }}>
                <Typography variant="body2" gutterBottom>
                  {day === 0 ? 'First Run' : `${day} Days Out`}
                </Typography>

                {grouped[day].map((action, actionIndex) => {
                  const decomAction = decomActions.find(
                    (a) => action.actionType === a.value
                  );
                  if (!decomAction) return null;
                  const summary = decomNarratives[decomAction.value];

                  const summaryParams = {
                    ...action,
                    gPanelTier: currentTier,
                  };
                  const summaryDetail =
                    typeof summary === 'function'
                      ? summary(summaryParams)
                      : summary;
                  return (
                    <SharedDetailAccordion
                      key={decomAction.value}
                      index={actionIndex.toString()}
                      title={decomAction.label.replace(/_/g, ' ')}
                      detail={
                        <Typography variant="body2">
                          {summaryDetail}
                          {action.userToNotify?.length ? (
                            <>
                              {' '}
                              <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {formatStringList(action.userToNotify)}
                              </Box>{' '}
                              will be notified on completion.
                            </>
                          ) : null}
                        </Typography>
                      }
                    />
                  );
                })}
              </Box>
            </Box>
          )),
        },
      ],
    },
  ];

  return <SharedReviewLayout sections={sections} />;
}
